/**
 * Scroll-glow — stamps `is-scrolling` on <html> while ANY scroll container is
 * actively scrolling, then removes it after a short idle debounce. The CSS in
 * src/index.css keys `html.is-scrolling … ::-webkit-scrollbar-thumb` off that
 * class to brighten the accent scrollbar to its full gradient and pulse its
 * glow during scroll. It is token-driven (`--primary`/`--cyan-*`), so the
 * effect follows the active theme (light/dark) and accent palette for free.
 *
 * Why capture-phase on `window`: the `scroll` event does NOT bubble, so a
 * single delegated listener only sees every nested scroller if it runs in the
 * capture phase from the top of the tree. One listener, all scrollers.
 */

/** Idle window after the last scroll tick before the glow fades (ms). */
export const SCROLL_IDLE_MS = 600;

/** Class toggled on <html> while a scroll is in flight. */
export const SCROLLING_CLASS = 'is-scrolling';

/**
 * Begin tracking scroll activity. Idempotent-friendly: returns a teardown
 * function that removes the listener and clears any pending idle timer. Safe
 * to call in non-DOM environments (SSR / unit setup) — it no-ops and returns
 * a no-op teardown.
 */
export function initScrollGlow(idleMs: number = SCROLL_IDLE_MS): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const root = document.documentElement;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;

  const onScroll = (): void => {
    if (!root.classList.contains(SCROLLING_CLASS)) {
      root.classList.add(SCROLLING_CLASS);
    }
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      root.classList.remove(SCROLLING_CLASS);
      idleTimer = undefined;
    }, idleMs);
  };

  // Capture phase so scroll from every nested container reaches this one
  // listener (scroll does not bubble); passive so we never block the scroll.
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });

  return () => {
    window.removeEventListener('scroll', onScroll, { capture: true });
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    root.classList.remove(SCROLLING_CLASS);
  };
}
